import { ParagraphLLMFeedback } from '../../db/indexedDb';
import { LLMOrchestrator } from './orchestrator';
import { buildInvoker } from './providerFactory';
import { callOpenRouter } from './openrouter';
import {
  readLLMEnabledFlag,
  readLLMTimeouts,
  getLLMLogger,
} from './config';

// ============= 停用词表（保留这些，不挖空）=============
const FUNCTION_WORDS = new Set([
  'a', 'an', 'the',
  'in', 'on', 'at', 'to', 'for', 'with', 'by', 'from', 'of', 'about', 'into', 'through',
  'and', 'but', 'or', 'so', 'because', 'although', 'while', 'if', 'when', 'as', 'than',
  'it', 'this', 'that', 'these', 'those', 'they', 'we', 'he', 'she', 'i', 'you',
  'is', 'are', 'was', 'were', 'be', 'been', 'being', 'am',
  'have', 'has', 'had', 'do', 'does', 'did',
  'can', 'could', 'will', 'would', 'should', 'may', 'might', 'must',
  'not', 'no', 'also', 'however', 'therefore', 'thus', 'moreover', 'furthermore',
  'which', 'who', 'whom', 'whose', 'what', 'where', 'how', 'why',
  'there', 'here', 'then', 'now', 'very', 'just', 'only', 'even', 'still',
]);

type EvaluateInput = {
  question?: string;
  reference: string;
  userAnswer: string;
};

const craftSuggestions = (reference: string, answer: string): string[] => {
  const suggestions: string[] = [];
  if (!/however|moreover|therefore|in addition/i.test(answer)) {
    suggestions.push('尝试增加连接词（however/moreover/therefore）让段落更连贯');
  }
  if (!/because|since|as a result/i.test(answer)) {
    suggestions.push('补充因果句式，例如 “because”/“as a result” 强化逻辑');
  }
  if (answer.split(/[,.;]/).some((p) => p.trim().length < 8)) {
    suggestions.push('合并过短的句子，使用从句或非谓语让句子更顺');
  }
  if (!suggestions.length) {
    suggestions.push('整体表达顺畅，可继续关注用词多样性和细节描写');
  }
  if (suggestions.length > 3) return suggestions.slice(0, 3);
  return suggestions;
};

const rewriteAnswer = (answer: string, reference: string): string => {
  const trimmed = answer.trim();
  if (trimmed.length > 0) return trimmed;
  // fallback：若用户未输入内容，返回参考答案短版
  return reference.slice(0, 260);
};

const parseJSONSafely = (text: string | null) => {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (err) {
    return null;
  }
};

export async function hasLLMSupport(): Promise<boolean> {
  return readLLMEnabledFlag();
}

/**
 * LLM 智能挖空：分析原文和用户漏掉的内容，返回高价值表达列表
 * 优先挖：搭配词组、高级表达、易错点
 * 保留：连接词、冠词、基础介词
 */
export async function generateSmartClozeBlanks(
  reference: string,
  missingUnits: string[]
): Promise<string[] | null> {
  // 如果 LLM 未启用，返回 null 让调用方降级
  if (!readLLMEnabledFlag()) {
    return filterContentPhrases(missingUnits);
  }

  const timeouts = readLLMTimeouts();
  const logger = getLLMLogger();
  
  const prompt = `你是英语写作教练。从原文中选出 5-6 个最值得练习的表达用于填空练习。

规则：
1. 优先选：搭配词组(2-4词)、高级表达、学生漏掉的重点
2. 不要选：单个冠词、单个介词、简单连接词
3. 每个表达 2-4 个词为佳

原文：
${reference}

学生漏掉的内容：
${missingUnits.slice(0, 8).join(', ')}

只返回 JSON，不要其他内容：
{"blanks": ["expression 1", "expression 2", ...]}`;

  try {
    // OpenRouter 优先（使用 gemini-3-flash-preview）
    const orchestrator = new LLMOrchestrator<string[]>(
      [
        buildInvoker('openrouter', async () => {
          const text = await callOpenRouter(prompt, { jsonMode: true });
          const parsed = parseJSONSafely(text);
          if (parsed?.blanks && Array.isArray(parsed.blanks)) {
            return parsed.blanks.filter((b: unknown) => typeof b === 'string' && b.length >= 2);
          }
          return null;
        }),
      ],
      { totalTimeoutMs: timeouts.totalTimeoutMs, logger }
    );

    const result = await orchestrator.run();
    logger('[cloze] LLM smart blanks success', { provider: result.provider, count: result.data.length });
    return result.data;
  } catch (err) {
    logger('[cloze] LLM smart blanks failed', err);
    // 降级：本地过滤
    return filterContentPhrases(missingUnits);
  }
}

/**
 * 本地降级：过滤出实义词/短语
 */
function filterContentPhrases(units: string[]): string[] {
  return units
    .filter((unit) => {
      const words = unit.toLowerCase().split(/\s+/);
      // 至少有一个实义词
      return words.some((w) => !FUNCTION_WORDS.has(w) && w.length > 2);
    })
    .slice(0, 6);
}

export async function evaluateParagraphWithLLM(input: EvaluateInput): Promise<ParagraphLLMFeedback> {
  // 测试环境直接走本地 mock，避免外部 LLM 依赖导致超时或无效 URL。
  const inVitest =
    (typeof import.meta !== 'undefined' && (import.meta as any).vitest) ||
    (typeof process !== 'undefined' && Boolean((process as any).env?.VITEST));
  if (inVitest) {
    const improvedAnswer = rewriteAnswer(input.userAnswer, input.reference);
    const suggestions = craftSuggestions(input.reference, input.userAnswer);
    return {
      improvedAnswer,
      suggestions,
      provider: 'mock-llm-offline',
      latencyMs: 0,
    };
  }

  const timeouts = readLLMTimeouts();
  const logger = getLLMLogger();

  // OpenRouter 优先（使用 gemini-3-flash-preview），本地 mock 作为降级
  const orchestrator = new LLMOrchestrator<ParagraphLLMFeedback>(
    [
      buildInvoker('openrouter', async () => {
        const text = await callOpenRouter(
          `你是雅思写作点评老师。根据参考答案和学生答案返回 JSON。
格式: {"improvedAnswer":"改进后的答案","suggestions":["建议1","建议2"]}
建议不超过3条。
题目: ${input.question ?? ''}
参考答案: ${input.reference}
学生答案: ${input.userAnswer}`,
          { jsonMode: true }
        );
        const parsed = parseJSONSafely(text);
        if (parsed?.improvedAnswer && Array.isArray(parsed?.suggestions)) {
          return {
            improvedAnswer: parsed.improvedAnswer,
            suggestions: parsed.suggestions,
            provider: 'openrouter',
          };
        }
        return null;
      }),
      buildInvoker('mock-llm-offline', async () => {
        const start = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
        await new Promise((resolve) => setTimeout(resolve, 20));
        const improvedAnswer = rewriteAnswer(input.userAnswer, input.reference);
        const suggestions = craftSuggestions(input.reference, input.userAnswer);
        const end = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
        return {
          improvedAnswer,
          suggestions,
          provider: 'mock-llm-offline',
          latencyMs: Math.round(end - start),
        };
      }),
    ],
    { totalTimeoutMs: timeouts.totalTimeoutMs, logger }
  );

  const result = await orchestrator.run();
  return result.data;
}
